<script setup lang="ts">

import {computed, inject} from 'vue';
import {SvgIcon} from '../../svg.ts';
import FileTreeItem from './FileTreeItem.vue';
import {FILE_DIFF_STATUS} from '../types/file_tree.ts';
import type {TreeNode} from '../types/file_tree.ts';
import type {Store} from '../store.ts';
import {saveDiffFilterState} from '../diffFilterPersistence.ts';

const store: Store = inject('store');

const {tree, sidebarWidth, handleStartResize} = defineProps<{
  tree: TreeNode[],
  sidebarWidth: number,
  handleStartResize: (e: MouseEvent) => void,
}>();

const searchQuery = defineModel('searchQuery', {
  type: String,
  default: '',
});

function toggleShowUnchangedSheets() {
  store.diffFilterConfig.showUnchangedSheets.value = !store.diffFilterConfig.showUnchangedSheets.value;
  saveDiffFilterState(store);
}

function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
  if (!query) return nodes;
  const lowerQuery = query.toLowerCase();
  return nodes
    .map((node) => {
      const nameMatches = node.name.toLowerCase().includes(lowerQuery);

      if ('children' in node && node.children) {
        const filteredChildren = filterTree(node.children as TreeNode[], query);
        if (filteredChildren.length > 0) {
          return {...node, children: filteredChildren};
        }
      }

      if (nameMatches) {
        return node;
      }
      return null;
    })
    .filter(Boolean) as TreeNode[];
}

const filteredTree = computed(() => filterTree(tree, searchQuery.value));
const hasActiveSearch = computed(() => searchQuery.value.length > 0);

// Only show the unchanged-sheets toggle when the tree contains a multi-page
// schematic that can have unchanged pages, including project groups with
// unchanged source documents.
const hasMultiPageProjects = computed(() => {
  const hasUnchangedDocument = (nodes: TreeNode[]): boolean => nodes.some((node) =>
    (node.ty === 'sch-diff' && node.status === FILE_DIFF_STATUS.UNCHANGED) ||
    ('children' in node && node.children && hasUnchangedDocument(node.children as TreeNode[])),
  );
  const check = (nodes: TreeNode[]): boolean => {
    for (const node of nodes) {
      if (node.ty === 'project-group' && hasUnchangedDocument(node.children)) return true;
      if (
        (node.ty === 'proj-diff' || node.ty === 'multi-page-diff') &&
        node.status !== FILE_DIFF_STATUS.ADDED &&
        node.status !== FILE_DIFF_STATUS.DELETED
      ) return true;
      if ('children' in node && node.children && check(node.children as TreeNode[])) return true;
    }
    return false;
  };
  return check(tree);
});
</script>

<template>
  <div
    :style="{ maxWidth: sidebarWidth + 'px' }"
    class="tw:flex tw:sticky tw:max-h-[calc(100vh-47px)] tw:top-[47px] tw:w-full"
  >
    <div
      class="tw:overflow-y-auto tw:h-full tw:w-full"
    >
      <div class="tw:relative tw:px-2 tw:pt-2 tw:pb-2 tw:-mx-2">
        <SvgIcon
          name="octicon-search"
          :size="14"
          class="tw:absolute tw:left-4 tw:top-0 tw:bottom-0 tw:m-auto tw:text-text-light-3 tw:pointer-events-none"
        />
        <input
          :value="searchQuery"
          type="text"
          class="file-tree-search"
          placeholder="Filter files..."
          @input="searchQuery = ($event.target as HTMLInputElement).value"
        >
        <button
          v-if="hasActiveSearch"
          class="tw:absolute tw:right-3 tw:top-0 tw:bottom-0 tw:my-auto tw:flex tw:items-center tw:h-fit tw:border-0 tw:bg-transparent tw:cursor-pointer tw:text-text-light-3 tw:hover:text-text tw:p-0.5"
          @click="searchQuery = ''"
        >
          <SvgIcon
            name="octicon-x"
            :size="14"
          />
        </button>
      </div>
      <button
        v-if="hasMultiPageProjects"
        class="unchanged-sheets-toggle"
        :aria-pressed="store.diffFilterConfig.showUnchangedSheets.value"
        aria-label="Toggle unchanged schematic sheets"
        @click="toggleShowUnchangedSheets"
      >
        {{ store.diffFilterConfig.showUnchangedSheets.value ? 'Hide' : 'Show' }} Unchanged Schematic Sheets
      </button>
      <div
        v-if="hasActiveSearch && filteredTree.length === 0"
        class="tw:p-4 tw:text-center tw:text-text-light-3 tw:text-sm"
      >
        No files match "{{ searchQuery }}"
      </div>
      <FileTreeItem
        v-for="item in filteredTree"
        :key="item.headPath || item.basePath"
        :item="item"
        :top-level="true"
        :force-expand="hasActiveSearch"
      />
    </div>
    <div
      class="resize-handle tw:cursor-col-resize tw:h-full tw:w-3"
      @mousedown="handleStartResize"
    />
  </div>
</template>

<style scoped>
  .file-tree-search {
    padding: 4px 28px;
    width: 100%;
    font-size: 0.85rem;
    border: 1px solid var(--color-secondary);
    border-radius: var(--border-radius);
    background: transparent;
    color: var(--color-text);
    outline: none;
  }

  .file-tree-search:focus {
    border-color: var(--color-primary);
  }

  .file-tree-search::placeholder {
    color: var(--color-text-light-3);
  }

  .resize-handle {
    border-right: 1px solid var(--color-secondary);
  }

  .resize-handle:hover {
    background: var(--color-hover);
  }

  @reference "#index.css";

  .unchanged-sheets-toggle {
    @apply tw:w-full tw:text-left tw:text-xs tw:text-text-light-3 tw:bg-transparent tw:border-0 tw:px-2 tw:pb-2 tw:pt-0 tw:cursor-pointer;
  }

  .unchanged-sheets-toggle:hover {
    @apply tw:text-text;
  }
</style>
